import React, { useState, useEffect } from "react";
import { FormControl, InputLabel, OutlinedInput } from "@mui/material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import BlueButton from "../../../../components/BlueButton/BlueButton";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import api from "../../../../utils/api";

const ProductsSection = ({ isMobile }) => {
  // State for each section separately
  const [latestProducts, setLatestProducts] = useState({
    products_count: "",
    date_from: dayjs(""),
    date_to: dayjs(""),
    type: "",
  });

  const [bestSellerProducts, setBestSellerProducts] = useState({
    products_count: "",
    date_from: dayjs(""),
    date_to: dayjs(""),
    type: "",
  });

  const [recommendProducts, setRecommendProducts] = useState({
    products_count: "",
    date_from: dayjs(""),
    date_to: dayjs(""),
    type: "",
  });

  // Loading state
  const [loading, setLoading] = useState(false);

  // Fetch system settings on component mount
  useEffect(() => {
    fetchSystemSettings();
  }, []);

  // Function to fetch system settings from API
  const fetchSystemSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get("/system-setting");

      if (res.data && res.data.data) {
        // Process each section separately
        res.data.data.forEach((item) => {
          if (item.type === "latest") {
            setLatestProducts({
              products_count: String(item.products_count || "17"),
              date_from: item.date_from
                ? dayjs(item.date_from)
                : dayjs("2025-03-25"),
              date_to: item.date_to ? dayjs(item.date_to) : dayjs("2026-03-25"),
              type: "latest",
            });
          } else if (item.type === "best_seller") {
            setBestSellerProducts({
              products_count: String(item.products_count || "20"),
              date_from: item.date_from
                ? dayjs(item.date_from)
                : dayjs("2025-03-25"),
              date_to: item.date_to ? dayjs(item.date_to) : dayjs("2026-03-25"),
              type: "best_seller",
            });
          } else if (item.type === "recommend") {
            setRecommendProducts({
              products_count: String(item.products_count || "30"),
              date_from: item.date_from
                ? dayjs(item.date_from)
                : dayjs("2025-03-25"),
              date_to: item.date_to ? dayjs(item.date_to) : dayjs("2026-03-25"),
              type: "recommend",
            });
          }
        });
      }
    } catch (error) {
      console.error("Error fetching system settings:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to save system settings
  const saveSystemSettings = async () => {
    try {
      setLoading(true);

      // Format the data for API to match Postman form structure
      const formattedData = {
        products_count: [
          parseInt(latestProducts.products_count) || 20, // default to 20 if not provided
          parseInt(bestSellerProducts.products_count) || 30, // default to 30 if not provided
          parseInt(recommendProducts.products_count) || 20, // default to 20 if not provided
        ],
        date_from: [
          latestProducts.date_from.format("YYYY-MM-DD"),
          bestSellerProducts.date_from.format("YYYY-MM-DD"),
          recommendProducts.date_from.format("YYYY-MM-DD"),
        ],
        date_to: [
          latestProducts.date_to.format("YYYY-MM-DD"),
          bestSellerProducts.date_to.format("YYYY-MM-DD"),
          recommendProducts.date_to.format("YYYY-MM-DD"),
        ],
        type: ["latest", "best_seller", "recommend"],
      };

      await api.post("/update-system-setting", formattedData);
      toast.success("تم الحفظ بنجاح");
    } catch (error) {
      console.error("Error saving system settings:", error);
      toast.error("حدث خطأ أثناء الحفظ");
    } finally {
      setLoading(false);
    }
  };

  // Handle product count change for Latest Products
  const handleLatestProductCountChange = (e) => {
    const value = e.target.value;
    // Remove non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, "");
    setLatestProducts((prev) => ({
      ...prev,
      products_count: numericValue,
    }));
  };

  // Handle product count change for Best Seller Products
  const handleBestSellerProductCountChange = (e) => {
    const value = e.target.value;
    // Remove non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, "");
    setBestSellerProducts((prev) => ({
      ...prev,
      products_count: numericValue,
    }));
  };

  // Handle product count change for Recommend Products
  const handleRecommendProductCountChange = (e) => {
    const value = e.target.value;
    // Remove non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, "");
    setRecommendProducts((prev) => ({
      ...prev,
      products_count: numericValue,
    }));
  };

  // Reusable date field component
  const DateFieldWithLabel = ({ label, isMobile, value, onChange }) => (
    <FormControl
      sx={{
        width: isMobile ? "100%" : "316px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexDirection: "row",
      }}
      variant="outlined"
    >
      <InputLabel
        htmlFor="outlined-adornment-password"
        shrink
        sx={{
          background: "white",
          transformOrigin: "right top",
          transform: "translate(14px, -9px) scale(0.75)",
          right: 24,
          left: "auto",
          paddingLeft: "4px",
          paddingRight: "4px",
        }}
      >
        {label}
      </InputLabel>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        sx={{
          p: 0,
          width: "100%",
        }}
      >
        <DemoContainer
          components={["DatePicker"]}
          sx={{
            p: 0,
            width: "100%",
          }}
        >
          <DatePicker
            sx={{ width: "100%" }}
            value={value}
            onChange={onChange}
          />
        </DemoContainer>
      </LocalizationProvider>
    </FormControl>
  );

  return (
    <>
      <p className="view-title">اعدادات ظهور المنتجات</p>

      <div className="options-container">
        {/* Latest Products Section */}
        <div className="view-option">
          <span className="options-title">قسم احدث المنتجات</span>
          <div className="control-group">
            <FormControl
              sx={{ width: isMobile ? "100%" : "316px" }}
              variant="outlined"
            >
              <InputLabel
                htmlFor="product-count-latest"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                عدد المنتجات
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="product-count-latest"
                type="text"
                notched
                value={latestProducts.products_count}
                onChange={handleLatestProductCountChange}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </FormControl>

            <DateFieldWithLabel
              label="التاريخ من"
              isMobile={isMobile}
              value={latestProducts.date_from}
              onChange={(value) =>
                setLatestProducts((prev) => ({ ...prev, date_from: value }))
              }
            />

            <DateFieldWithLabel
              label="التاريخ الي"
              isMobile={isMobile}
              value={latestProducts.date_to}
              onChange={(value) =>
                setLatestProducts((prev) => ({ ...prev, date_to: value }))
              }
            />
          </div>
        </div>

        {/* Best Seller Products Section */}
        <div className="view-option">
          <span className="options-title">قسم الاكثر مبيعا</span>
          <div className="control-group">
            <FormControl
              sx={{ width: isMobile ? "100%" : "316px" }}
              variant="outlined"
            >
              <InputLabel
                htmlFor="product-count-best-seller"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                عدد المنتجات
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="product-count-best-seller"
                type="text"
                notched
                value={bestSellerProducts.products_count}
                onChange={handleBestSellerProductCountChange}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </FormControl>

            <DateFieldWithLabel
              label="التاريخ من"
              isMobile={isMobile}
              value={bestSellerProducts.date_from}
              onChange={(value) =>
                setBestSellerProducts((prev) => ({ ...prev, date_from: value }))
              }
            />

            <DateFieldWithLabel
              label="التاريخ الي"
              isMobile={isMobile}
              value={bestSellerProducts.date_to}
              onChange={(value) =>
                setBestSellerProducts((prev) => ({ ...prev, date_to: value }))
              }
            />
          </div>
        </div>

        {/* Recommend Products Section */}
        <div className="view-option">
          <span className="options-title">قسم منتجات مقترحة</span>
          <div className="control-group">
            <FormControl
              sx={{ width: isMobile ? "100%" : "316px" }}
              variant="outlined"
            >
              <InputLabel
                htmlFor="product-count-recommend"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                عدد المنتجات
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="product-count-recommend"
                type="text"
                notched
                value={recommendProducts.products_count}
                onChange={handleRecommendProductCountChange}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </FormControl>

            <DateFieldWithLabel
              label="التاريخ من"
              isMobile={isMobile}
              value={recommendProducts.date_from}
              onChange={(value) =>
                setRecommendProducts((prev) => ({ ...prev, date_from: value }))
              }
            />

            <DateFieldWithLabel
              label="التاريخ الي"
              isMobile={isMobile}
              value={recommendProducts.date_to}
              onChange={(value) =>
                setRecommendProducts((prev) => ({ ...prev, date_to: value }))
              }
            />
          </div>
        </div>

        <BlueButton
          text={loading ? "جاري الحفظ..." : "حفظ"}
          size={isMobile ? "full" : "large"}
          variant="fill"
          onClick={saveSystemSettings}
          disabled={loading}
        />
      </div>
    </>
  );
};

export default ProductsSection;
